'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

import { diveSites, type DiveSite } from '../diveSitesData';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilterState {
  skillLevel: string;
  access: string;
  searchQuery: string;
}

interface RegionalDiveSitesPageProps {
  regionId: string;
  regionLabel: string;
  regionHref: string;
  guideHref: string;
  guideLabel: string;
  heroDescription: string;
  heroImage: string;
  heroImageAlt: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CYAN = '#6FD3E0';
const ORANGE = '#FF8A3D';
const BG_DARK = '#07111C';
const BG_CARD = '#0F2234';
const BG_SECTION = '#0E1C2B';
const TEXT_PRIMARY = '#EAF4F8';
const TEXT_SECONDARY = '#C8DDE6';
const BORDER = 'rgba(111,211,224,0.12)';

const inputStyle = {
  backgroundColor: '#0B1622',
  color: TEXT_PRIMARY,
  border: '1px solid rgba(111,211,224,0.2)',
};

// ─── Branded icon maps (matching DiveSitesMasterInteractive) ──────────────────

const TYPE_BRANDED_ICONS: Record<string, string> = {
  Wreck: '/assets/images/cyprus-diving-wreck-icon-1777190509956.png',
  Reef: '/assets/images/cyprus-diving-reef-icon-1777203842964.png',
  Cave: '/assets/images/cyprus-diving-cave-icon-1777203860707.png',
  Wall: '/assets/images/cyprus-diving-wall-dropoff-icon-1777190565359.png',
  'Artificial Reef': '/assets/images/cyprus-diving-artificial-reef-icon-1777203921818.png',
  Shore: '/assets/images/cyprus-diving-beginner-shore-icon-1777203882444.png',
  Technical: '/assets/images/cyprus-diving-technical-icon-1777203828798.png',
};

const TYPE_BRANDED_ALT: Record<string, string> = {
  Wreck: 'Cyprus wreck diving icon — sunken ship dive site',
  Reef: 'Cyprus reef diving icon — coral reef dive site',
  Cave: 'Cyprus cave diving icon — underwater cave and cavern dive site',
  Wall: 'Cyprus wall and drop-off diving icon — vertical underwater wall dive site',
  'Artificial Reef': 'Cyprus artificial reef diving icon — man-made reef structure dive site',
  Shore: 'Cyprus shore diving icon — beginner-friendly shore entry dive site',
  Technical: 'Cyprus technical diving icon — advanced technical dive site',
};

const TYPE_GRADIENTS: Record<string, string> = {
  Wreck: 'linear-gradient(135deg,#1a2a3a 0%,#0d1f30 100%)',
  Reef: 'linear-gradient(135deg,#0d2a2a 0%,#071c1c 100%)',
  Cave: 'linear-gradient(135deg,#1a1a2e 0%,#0d0d1f 100%)',
  Wall: 'linear-gradient(135deg,#1a2030 0%,#0d1525 100%)',
  'Artificial Reef': 'linear-gradient(135deg,#1a2a1a 0%,#0d1f0d 100%)',
  Shore: 'linear-gradient(135deg,#0d2030 0%,#071525 100%)',
};

const TYPE_ACCENT: Record<string, string> = {
  Wreck: '#FF8A3D',
  Reef: '#6FD3E0',
  Cave: '#a78bfa',
  Wall: '#38bdf8',
  'Artificial Reef': '#4ade80',
  Shore: '#6FD3E0',
};

const LEVEL_COLORS: Record<string, string> = {
  'Open Water': '#4ade80',
  Advanced: '#FF8A3D',
  Technical: '#f87171',
};

const ACCESS_ICONS: Record<string, string> = {
  Shore: 'MapPinIcon',
  Boat: 'AnchorIcon',
  'Shore or Boat': 'ArrowsRightLeftIcon',
};

// ─── Site card ────────────────────────────────────────────────────────────────

function SiteCard({ site }: { site: DiveSite }) {
  const primaryType = site.diveType[0];
  const gradient = TYPE_GRADIENTS[primaryType] ?? TYPE_GRADIENTS.Reef;
  const accent = TYPE_ACCENT[primaryType] ?? CYAN;
  const levelColor = LEVEL_COLORS[site.minLevel] ?? CYAN;
  const allTypes = site.diveType;

  return (
    <div
      style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER}` }}
      className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group flex flex-col"
    >
      {/* Icon header with branded icons */}
      <div
        style={{ background: gradient, borderBottom: `1px solid ${BORDER}` }}
        className="relative h-32 flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ background: `radial-gradient(circle at 70% 30%, ${accent} 0%, transparent 60%)` }}
        />
        {/* All dive type branded icons side by side */}
        <div className="flex items-center gap-2">
          {allTypes.slice(0, 2).map((type) => {
            const typeAccent = TYPE_ACCENT[type] ?? CYAN;
            const brandedIcon = TYPE_BRANDED_ICONS[type];
            const brandedAlt = TYPE_BRANDED_ALT[type] ?? `${type} dive site icon`;
            const iconSize = allTypes.length > 1 ? 52 : 64;
            const imgSize = allTypes.length > 1 ? 32 : 40;
            return (
              <div
                key={type}
                style={{
                  width: iconSize,
                  height: iconSize,
                  borderRadius: '50%',
                  border: `2px solid ${typeAccent}30`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${typeAccent}12`,
                }}
              >
                {brandedIcon ? (
                  <Image
                    src={brandedIcon}
                    alt={brandedAlt}
                    width={imgSize}
                    height={imgSize}
                    className="object-contain"
                    style={{ filter: 'brightness(1.05) drop-shadow(0 2px 6px rgba(0,0,0,0.35))' }}
                  />
                ) : (
                  <Icon name="SparklesIcon" size={imgSize - 8} style={{ color: typeAccent }} />
                )}
              </div>
            );
          })}
        </div>
        <div className="absolute top-3 left-3 flex flex-wrap gap-1">
          {site.diveType.slice(0, 2).map((t) => (
            <span
              key={t}
              style={{
                backgroundColor: `${TYPE_ACCENT[t] ?? CYAN}20`,
                color: TYPE_ACCENT[t] ?? CYAN,
                border: `1px solid ${TYPE_ACCENT[t] ?? CYAN}40`,
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 7px',
                borderRadius: 4,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <h3
          style={{ color: TEXT_PRIMARY }}
          className="text-lg font-headline font-bold mb-1 group-hover:text-cyan-300 transition-colors leading-snug"
        >
          {site.name}
        </h3>

        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
          <span style={{ color: TEXT_SECONDARY, fontSize: 12 }} className="flex items-center gap-1">
            <Icon name={ACCESS_ICONS[site.access] ?? 'MapPinIcon'} size={12} style={{ color: CYAN }} />
            {site.access}
          </span>
          <span style={{ color: levelColor, fontSize: 12, fontWeight: 600 }}>{site.minLevel}</span>
        </div>

        <p style={{ color: TEXT_SECONDARY, fontSize: 13 }} className="mb-4 leading-relaxed line-clamp-3 flex-1">
          {site.description}
        </p>

        <div
          style={{ backgroundColor: 'rgba(111,211,224,0.04)', border: `1px solid ${BORDER}` }}
          className="rounded-lg p-3 mb-4 grid grid-cols-2 gap-2"
        >
          <div className="flex items-center gap-2">
            <Icon name="ArrowTrendingDownIcon" size={14} style={{ color: CYAN }} />
            <div>
              <div style={{ color: TEXT_SECONDARY, fontSize: 10 }}>Max Depth</div>
              <div style={{ color: TEXT_PRIMARY, fontSize: 12, fontWeight: 600 }}>{site.maxDepth}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="EyeIcon" size={14} style={{ color: CYAN }} />
            <div>
              <div style={{ color: TEXT_SECONDARY, fontSize: 10 }}>Visibility</div>
              <div style={{ color: TEXT_PRIMARY, fontSize: 12, fontWeight: 600 }}>{site.visibility}</div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          {site.gps ? (
            <div className="flex items-start gap-2">
              <Icon name="MapPinIcon" size={13} style={{ color: CYAN, marginTop: 1, flexShrink: 0 }} />
              <span style={{ color: TEXT_SECONDARY, fontSize: 11, fontFamily: 'monospace' }}>{site.gps}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Icon name="MapPinIcon" size={13} style={{ color: '#4A7A8A' }} />
              <span style={{ color: '#4A7A8A', fontSize: 11 }}>Coordinates not currently available</span>
            </div>
          )}
        </div>

        <div className="mb-4">
          <p style={{ color: TEXT_PRIMARY, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }} className="mb-2">
            Highlights
          </p>
          <ul className="space-y-1">
            {site.highlights.slice(0, 4).map((h, i) => (
              <li key={i} className="flex items-start gap-2">
                <span style={{ color: accent, fontSize: 10, marginTop: 3, flexShrink: 0 }}>▸</span>
                <span style={{ color: TEXT_SECONDARY, fontSize: 12 }}>{h}</span>
              </li>
            ))}
          </ul>
        </div>

        {site.marineLife && site.marineLife.length > 0 && (
          <div>
            <p style={{ color: TEXT_PRIMARY, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }} className="mb-2">
              Marine Life
            </p>
            <div className="flex flex-wrap gap-1">
              {site.marineLife.slice(0, 5).map((m, i) => (
                <span
                  key={i}
                  style={{
                    backgroundColor: 'rgba(111,211,224,0.08)',
                    color: CYAN,
                    border: '1px solid rgba(111,211,224,0.2)',
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}
                >
                  {m}
                </span>
              ))}
              {site.marineLife.length > 5 && (
                <span style={{ backgroundColor: 'rgba(255,255,255,0.04)', color: TEXT_SECONDARY, fontSize: 10, padding: '2px 6px', borderRadius: 4 }}>
                  +{site.marineLife.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const RegionalDiveSitesPage = ({
  regionId,
  regionLabel,
  regionHref,
  guideHref,
  guideLabel,
  heroDescription,
  heroImage,
  heroImageAlt,
}: RegionalDiveSitesPageProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ skillLevel: 'all', access: 'all', searchQuery: '' });
  const [filtered, setFiltered] = useState<DiveSite[]>([]);

  const regionSites = diveSites.filter((s) => s.region === regionId);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const applyFilters = useCallback(() => {
    let result = regionSites;
    if (filters.skillLevel !== 'all') result = result.filter((s) => s.minLevel === filters.skillLevel);
    if (filters.access !== 'all') result = result.filter((s) => s.access === filters.access);
    if (filters.searchQuery.trim()) {
      const q = filters.searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          (s.marineLife ?? []).some((m) => m.toLowerCase().includes(q))
      );
    }
    setFiltered(result);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, regionId]);

  useEffect(() => {
    if (isHydrated) applyFilters();
  }, [filters, isHydrated, applyFilters]);

  const handleReset = () => setFilters({ skillLevel: 'all', access: 'all', searchQuery: '' });

  if (!isHydrated) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: BG_DARK }}>
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 rounded w-1/2" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-96 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG_DARK }}>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative h-72 md:h-96">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={heroImage} alt={heroImageAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(7,17,28,0.5) 0%, rgba(7,17,28,0.85) 100%)' }} />
          <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12">
            <nav className="flex items-center space-x-2 text-sm mb-4" aria-label="Breadcrumb">
              <Link href="/" style={{ color: CYAN }} className="hover:underline">Home</Link>
              <span style={{ color: '#4A7A8A' }}>/</span>
              <Link href="/dive-sites" style={{ color: CYAN }} className="hover:underline">Dive Sites</Link>
              <span style={{ color: '#4A7A8A' }}>/</span>
              <span style={{ color: TEXT_PRIMARY }}>{regionLabel}</span>
            </nav>
            <h1 style={{ color: TEXT_PRIMARY }} className="text-3xl md:text-5xl font-headline font-bold mb-3">
              Dive Sites in {regionLabel}, Cyprus
            </h1>
            <p style={{ color: TEXT_SECONDARY }} className="text-base md:text-lg max-w-3xl leading-relaxed">
              {heroDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Backlinks */}
      <section style={{ backgroundColor: BG_SECTION, borderBottom: `1px solid ${BORDER}` }} className="py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center gap-4">
            <Link
              href="/dive-sites"
              style={{ color: CYAN, border: '1px solid rgba(111,211,224,0.25)' }}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/5 transition-colors"
            >
              <Icon name="ArrowLeftIcon" size={16} />
              <span>All Dive Sites</span>
            </Link>
            <Link
              href={guideHref}
              style={{ color: ORANGE, border: '1px solid rgba(255,138,61,0.25)' }}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/5 transition-colors"
            >
              <Icon name="MapIcon" size={16} />
              <span>{guideLabel}</span>
            </Link>
            <div style={{ color: TEXT_SECONDARY }} className="text-sm ml-auto">
              <span style={{ color: CYAN }} className="font-bold">{regionSites.length}</span> dive sites in {regionLabel}
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div style={{ backgroundColor: BG_SECTION, border: `1px solid ${BORDER}` }} className="rounded-xl p-6 shadow-md">
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ color: TEXT_PRIMARY }} className="text-lg font-headline font-bold flex items-center space-x-2">
                <Icon name="FunnelIcon" size={20} style={{ color: CYAN }} />
                <span>Filter {regionLabel} Dive Sites</span>
              </h2>
              <button onClick={handleReset} style={{ color: TEXT_SECONDARY }} className="text-sm flex items-center space-x-1 hover:opacity-80 transition-opacity">
                <Icon name="ArrowPathIcon" size={14} />
                <span>Reset</span>
              </button>
            </div>
            <div className="space-y-4">
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: CYAN }} />
                <input
                  type="text"
                  placeholder="Search dive sites..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters((p) => ({ ...p, searchQuery: e.target.value }))}
                  style={inputStyle}
                  className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/40 placeholder-slate-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label style={{ color: TEXT_PRIMARY }} className="block text-sm font-semibold mb-2">Skill Level</label>
                  <select
                    value={filters.skillLevel}
                    onChange={(e) => setFilters((p) => ({ ...p, skillLevel: e.target.value }))}
                    style={inputStyle}
                    className="w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                  >
                    <option value="all">All Levels</option>
                    <option value="Open Water">Open Water</option>
                    <option value="Advanced">Advanced</option>
                    <option value="Technical">Technical</option>
                  </select>
                </div>
                <div>
                  <label style={{ color: TEXT_PRIMARY }} className="block text-sm font-semibold mb-2">Access Method</label>
                  <select
                    value={filters.access}
                    onChange={(e) => setFilters((p) => ({ ...p, access: e.target.value }))}
                    style={inputStyle}
                    className="w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                  >
                    <option value="all">All Access</option>
                    <option value="Shore">Shore</option>
                    <option value="Boat">Boat</option>
                    <option value="Shore or Boat">Shore or Boat</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dive Sites Grid */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="MagnifyingGlassIcon" size={48} className="mx-auto mb-4" style={{ color: '#4A7A8A' }} />
              <h3 style={{ color: TEXT_PRIMARY }} className="text-xl font-headline font-bold mb-2">No dive sites found</h3>
              <p style={{ color: TEXT_SECONDARY }} className="mb-4">Try adjusting your filters.</p>
              <button onClick={handleReset} style={{ backgroundColor: CYAN, color: BG_DARK }} className="px-6 py-2 rounded-lg font-semibold">
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              <p style={{ color: TEXT_SECONDARY }} className="text-sm mb-6">
                Showing <span style={{ color: TEXT_PRIMARY }} className="font-semibold">{filtered.length}</span> dive site{filtered.length !== 1 ? 's' : ''} in {regionLabel}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((site) => (
                  <SiteCard key={site.id} site={site} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Bottom backlinks */}
      <section style={{ backgroundColor: BG_SECTION, borderTop: `1px solid ${BORDER}` }} className="py-10">
        <div className="container mx-auto px-4 text-center">
          <h2 style={{ color: TEXT_PRIMARY }} className="text-2xl font-headline font-bold mb-4">Explore More</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/dive-sites"
              style={{ backgroundColor: 'rgba(111,211,224,0.1)', color: CYAN, border: '1px solid rgba(111,211,224,0.25)' }}
              className="inline-flex items-center space-x-2 px-5 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              <Icon name="MapPinIcon" size={18} />
              <span>All Cyprus Dive Sites</span>
            </Link>
            <Link
              href={guideHref}
              style={{ backgroundColor: 'rgba(255,138,61,0.1)', color: ORANGE, border: '1px solid rgba(255,138,61,0.25)' }}
              className="inline-flex items-center space-x-2 px-5 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              <Icon name="MapIcon" size={18} />
              <span>{guideLabel}</span>
            </Link>
            <Link
              href="/dive-centres-directory"
              style={{ backgroundColor: 'rgba(111,211,224,0.06)', color: TEXT_SECONDARY, border: `1px solid ${BORDER}` }}
              className="inline-flex items-center space-x-2 px-5 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              <Icon name="BuildingStorefrontIcon" size={18} />
              <span>Find Dive Centres</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegionalDiveSitesPage;
